import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { collection, addDoc, doc, getDoc, getDocs, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/fbConfig';

const PromoteClassesModal = ({ isOpen, onClose, classes, sessions, terms, onSuccess }) => {
  const [promoteSettings, setPromoteSettings] = useState({
    currentSession: '',
    currentTerm: '',
    newSession: '',
    newTerm: ''
  });
  const [classesToPromote, setClassesToPromote] = useState([]);
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedClasses, setSelectedClasses] = useState({});

  // Update classes to promote when current session/term changes
  useEffect(() => {
    if (promoteSettings.currentSession && promoteSettings.currentTerm) {
        const classesToPromoteList = classes.filter(classItem => 
        classItem.sessionId === promoteSettings.currentSession && 
        classItem.termId === promoteSettings.currentTerm
        );
        setClassesToPromote(classesToPromoteList);
        
        // NEW: Initialize all classes as selected by default
        const initialSelected = {};
        classesToPromoteList.forEach(cls => {
        initialSelected[cls.id] = true;
        });
        setSelectedClasses(initialSelected);
    } else {
        setClassesToPromote([]);
        setSelectedClasses({});
    }
  }, [promoteSettings.currentSession, promoteSettings.currentTerm, classes]);

  // NEW: Handle checkbox toggle for individual classes
  const handleClassToggle = (classId) => {
    setSelectedClasses(prev => ({
        ...prev,
        [classId]: !prev[classId]
    }));
    };

    // NEW: Handle select/deselect all
    const handleSelectAll = () => {
    const allSelected = {};
    classesToPromote.forEach(cls => {
        allSelected[cls.id] = true;
    });
    setSelectedClasses(allSelected);
    };

    const handleDeselectAll = () => {
    const allDeselected = {};
    classesToPromote.forEach(cls => {
        allDeselected[cls.id] = false;
    });
    setSelectedClasses(allDeselected);
    };

    // NEW: Get count of selected classes
    const getSelectedCount = () => {
    return Object.values(selectedClasses).filter(selected => selected === true).length;
  };

  const handlePromoteInputChange = (e) => {
    const { name, value } = e.target;
    setPromoteSettings({ ...promoteSettings, [name]: value });
    setError('');
    setSuccessMessage('');
  };

  // Function to get students in a class
  const getStudentsInClass = async (classId, studentIds) => {
    // If we have the student IDs from the class document, use those
    if (studentIds && studentIds.length > 0) {
      const students = [];
      for (const studentId of studentIds) {
        const studentRef = doc(db, 'students', studentId);
        const studentDoc = await getDoc(studentRef);
        if (studentDoc.exists()) {
          students.push({ id: studentDoc.id, ...studentDoc.data() });
        }
      }
      return students;
    }
    
    // Fallback: fetch all students and filter
    const studentsSnap = await getDocs(collection(db, "students"));
    const allStudents = studentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return allStudents.filter(student => 
      student.enrollments?.some(e => e.classId === classId)
    );
  };

  const handleBulkPromote = async () => {
    if (!promoteSettings.currentSession || !promoteSettings.currentTerm || 
        !promoteSettings.newSession || !promoteSettings.newTerm) {
      setError("Please select current session, current term, new session, and new term");
      return;
    }

    if (classesToPromote.length === 0) {
      setError("No classes found with the selected current session and term");
      return;
    }

    // NEW: Filter only selected classes
    const classesToActuallyPromote = classesToPromote.filter(cls => selectedClasses[cls.id]);

    if (classesToActuallyPromote.length === 0) {
    setError("Please select at least one class to promote");
    return;
    }

    setPromoting(true);
    setError("");
    setSuccessMessage("");

    try {
      let promotedCount = 0;
      let failedClasses = [];

    //   for (const classItem of classesToPromote) {
      for (const classItem of classesToActuallyPromote) {
        try {
          console.log(`Promoting class: ${classItem.name}`);
          
          // 1. Get all students in this class using the students array from class document
          const studentIds = classItem.students || [];
          const students = await getStudentsInClass(classItem.id, studentIds);
          
          console.log(`Found ${students.length} students in class ${classItem.name}`);

          // 2. Get subjects for this class
          const subjects = classItem.subjects || [];

          // 3. Get teacher assignments for this class
          const usersSnap = await getDocs(collection(db, "users"));
          const teacherAssignments = {};
          
          usersSnap.docs.forEach(doc => {
            const teacher = { id: doc.id, ...doc.data() };
            if (['Teacher', 'Cashier', 'Admin'].includes(teacher.status) && teacher.isActive === true) {
              const teachingAssignments = teacher.teachingAssignments || [];
              teachingAssignments.forEach(assignment => {
                if (assignment.classId === classItem.id) {
                  teacherAssignments[assignment.subjectId] = teacher.id;
                }
              });
            }
          });

          // 4. Get current fees for this class from the first student
          let currentFees = 0;
          if (students.length > 0) {
            const firstStudent = students[0];
            const enrollment = firstStudent.enrollments?.find(e => e.classId === classItem.id);
            currentFees = enrollment?.fees || 0;
          }

          // 5. Create new class name (you can modify this logic)
          const newClassName = classItem.name;

          // 6. Create new class document
          const newClassData = {
            name: newClassName,
            section: classItem.sectionId,
            session: promoteSettings.newSession,
            term: promoteSettings.newTerm,
            formMaster: classItem.formMasterId || 'No Class Teacher',
            students: students.map(student => student.id),
            subjects: subjects.map(subject => ({ 
              id: subject.id, 
              teacherId: teacherAssignments[subject.id] || '' 
            })),
            createdAt: Timestamp.now(),
            promotedFrom: classItem.id,
            promotedOn: Timestamp.now()
          };

          const newClassRef = await addDoc(collection(db, 'classes'), newClassData);
          const newClassId = newClassRef.id;
          
          console.log(`Created new class: ${newClassId}`);

          // 7. Update student enrollments
          const psychomotorSkills = {
            handwriting: 'A',
            fluency: 'A',
            games: 'A',
            punctuality: 'A',
            neatness: 'A',
            politeness: 'A',
            cooperation: 'A',
            leadership: 'A',
            attentiveness: 'A',
            honesty: 'A',
          };

          // Process students one by one to avoid overwhelming Firestore
          for (const student of students) {
            try {
              const studentRef = doc(db, 'students', student.id);
              const studentDoc = await getDoc(studentRef);
              
              if (!studentDoc.exists()) {
                console.log(`Student ${student.id} not found, skipping`);
                continue;
              }
              
              const studentData = studentDoc.data();
              
              // Get the specific enrollment data for this student in this class
              const currentEnrollment = studentData.enrollments?.find(e => e.classId === classItem.id) || {};
              const studentFees = currentEnrollment.fees || currentFees;
              const studentPaid = currentEnrollment.paid || 0;
              
              const subjectsWithScores = subjects.map(subject => ({
                subjectId: subject.id,
                teacherId: teacherAssignments[subject.id] || '',
                scores: {
                  firstca: 0,
                  secondca: 0,
                  exam: 0,
                },
              }));

              const newEnrollment = {
                classId: newClassId,
                subjects: subjectsWithScores,
                psychomotorSkills,
                fees: studentFees, // Preserve the individual student's fee amount
                paid: 0, // Reset paid amount for new class
                Attendance: [],
                promotedFrom: classItem.id,
                promotedOn: Timestamp.now()
              };

              const updatedEnrollments = [...(studentData.enrollments || []), newEnrollment];

              await updateDoc(studentRef, { enrollments: updatedEnrollments });
              console.log(`Updated enrollment for student ${student.firstName} ${student.lastName}`);
            } catch (studentError) {
              console.error(`Error updating student ${student.id}:`, studentError);
            }
          }

          // 8. Update teacher assignments for the new class
          for (const userDoc of usersSnap.docs) {
            const teacher = { id: userDoc.id, ...userDoc.data() };
            if (['Teacher', 'Cashier', 'Admin'].includes(teacher.status) && teacher.isActive === true) {
              const oldAssignments = teacher.teachingAssignments || [];
              
              const subjectsToCarryOver = [];
              subjects.forEach(subject => {
                if (teacherAssignments[subject.id] === teacher.id) {
                  subjectsToCarryOver.push(subject.id);
                }
              });
              
              if (subjectsToCarryOver.length > 0) {
                const newAssignments = [
                  ...oldAssignments,
                  ...subjectsToCarryOver.map(subjectId => ({
                    classId: newClassId,
                    subjectId: subjectId
                  }))
                ];
                
                await updateDoc(doc(db, 'users', teacher.id), {
                  teachingAssignments: newAssignments
                });
                console.log(`Updated teacher assignments for ${teacher.firstName} ${teacher.lastName}`);
              }
            }
          }

          promotedCount++;
          console.log(`Successfully promoted class: ${classItem.name}`);
          
        } catch (error) {
          console.error(`Error promoting class ${classItem.name}:`, error);
          failedClasses.push(classItem.name);
        }
      }

      let message = '';
      if (failedClasses.length > 0) {
        message = `Successfully promoted ${promotedCount} classes. Failed: ${failedClasses.join(', ')}`;
        setError(message);
      } else {
        message = `Successfully promoted ${promotedCount} classes to ${promoteSettings.newTerm} term, ${promoteSettings.newSession} session!`;
        setSuccessMessage(message);
      }
      
      // Reset promote settings
      setPromoteSettings({
        currentSession: '',
        currentTerm: '',
        newSession: '',
        newTerm: ''
      });
      
      // Call onSuccess to refresh classes list
      if (onSuccess) {
        await onSuccess();
      }
      
      // Close modal after 3 seconds
      setTimeout(() => {
        onClose();
        setError('');
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      console.error("Error in bulk promotion:", error);
      setError("Failed to promote classes. Please try again.");
    } finally {
      setPromoting(false);
    }
  };

  const resetModal = () => {
    setPromoteSettings({
        currentSession: '',
        currentTerm: '',
        newSession: '',
        newTerm: ''
    });
    setError('');
    setSuccessMessage('');
    setClassesToPromote([]);
    setSelectedClasses({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={resetModal}
      contentLabel="Promote Classes"
      className="custom-modal wide-modal"
      overlayClassName="custom-modal-overlay"
    >
      <h2 className="size5 caps">Bulk Promote Classes</h2>
      
      <div className="modal-form modal-scroll">
        <h3 className="size6" style={{ marginTop: '10px' }}>Select Current Session & Term</h3>
        
        <label className="text-left">Current Session</label>
        <select
          name="currentSession"
          className="input-field"
          value={promoteSettings.currentSession}
          onChange={handlePromoteInputChange}
          disabled={promoting}
        >
          <option value="">Select Current Session</option>
          {sessions.map((session) => (
            <option key={session.id} value={session.id}>{session.name}</option>
          ))}
        </select>

        <label className="text-left">Current Term</label>
        <select
          name="currentTerm"
          className="input-field"
          value={promoteSettings.currentTerm}
          onChange={handlePromoteInputChange}
          disabled={promoting}
        >
          <option value="">Select Current Term</option>
          {terms.map((term) => (
            <option key={term.id} value={term.id}>{term.name}</option>
          ))}
        </select>

        {classesToPromote.length > 0 && !promoting && (
            <div className="info-item" style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '8px', marginTop: '10px' }}>
                {/* NEW: Select/Deselect All buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <p className="bold">
                    <i className="fas fa-check-square"></i> Select classes to promote ({getSelectedCount()} of {classesToPromote.length} selected):
                </p>
                <div>
                    <button 
                    onClick={handleSelectAll}
                    style={{ marginRight: '10px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}
                    className="small-button"
                    >
                    Select All
                    </button>
                    <button 
                    onClick={handleDeselectAll}
                    style={{ padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}
                    className="small-button"
                    >
                    Deselect All
                    </button>
                </div>
                </div>
                
                {/* NEW: Checkbox list for classes */}
                <ul style={{ marginTop: '5px', marginLeft: '0', maxHeight: '200px', overflowY: 'auto', listStyle: 'none', paddingLeft: '0' }}>
                {classesToPromote.map(cls => (
                    <li key={cls.id} style={{ marginBottom: '8px', padding: '5px', backgroundColor: selectedClasses[cls.id] ? '#e3f2fd' : 'white', borderRadius: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                        type="checkbox"
                        checked={selectedClasses[cls.id] || false}
                        onChange={() => handleClassToggle(cls.id)}
                        style={{ marginRight: '10px', cursor: 'pointer' }}
                        disabled={promoting}
                        />
                        <span>
                        <strong>{cls.name}</strong> 
                        {cls.section && <span style={{ color: '#666', marginLeft: '5px' }}> - {cls.section}</span>}
                        {cls.students && <span style={{ color: '#999', marginLeft: '5px', fontSize: '12px' }}>({cls.students.length} students)</span>}
                        </span>
                    </label>
                    </li>
                ))}
                </ul>
            </div>
            )}

        <h3 className="size6" style={{ marginTop: '20px' }}>Select New Session & Term</h3>

        <label className="text-left">New Session</label>
        <select
          name="newSession"
          className="input-field"
          value={promoteSettings.newSession}
          onChange={handlePromoteInputChange}
          disabled={promoting}
        >
          <option value="">Select New Session</option>
          {sessions.map((session) => (
            <option key={session.id} value={session.id}>{session.name}</option>
          ))}
        </select>

        <label className="text-left">New Term</label>
        <select
          name="newTerm"
          className="input-field"
          value={promoteSettings.newTerm}
          onChange={handlePromoteInputChange}
          disabled={promoting}
        >
          <option value="">Select New Term</option>
          {terms.map((term) => (
            <option key={term.id} value={term.id}>{term.name}</option>
          ))}
        </select>

        <div className="row align-center justify-center gap-15" style={{ marginTop: '20px' }}>
          <button
            className="login-button"
            onClick={resetModal}
            disabled={promoting}
          >
            Cancel
          </button>         
          <button
                className="login-button"
                onClick={handleBulkPromote}
                disabled={promoting || getSelectedCount() === 0 || !promoteSettings.newSession || !promoteSettings.newTerm}
                >
                {promoting ? (
                    <>
                    <i className="fas fa-spinner fa-spin"></i> Promoting...
                    </>
                ) : (
                    `Promote ${getSelectedCount()} Class(es)`
                )}
            </button>
        </div>
      </div>
    </Modal>
  );
};

export default PromoteClassesModal;